import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AffiliationRoleCheckboxes } from '@/components/contacts/affiliations/AffiliationRoleCheckboxes'
import { invalidateAffiliationQueries } from '@/components/contacts/affiliations/queryInvalidation'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { affiliationMutationMessage } from '@/lib/contacts/errors'
import type { Organization, Person, ProfileAffiliation } from '@/lib/contacts/types'

interface OrganizationsResponse {
  organizations: Organization[]
}

interface AffiliationResponse {
  affiliation: ProfileAffiliation
}

export type PersonAffiliationDialogState =
  | { kind: 'add' }
  | { kind: 'edit'; affiliation: ProfileAffiliation }

export function PersonAffiliationDialog({
  person,
  dialog,
  onClose,
}: {
  person: Person
  dialog: PersonAffiliationDialogState
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const editing = dialog.kind === 'edit'
  const [organizationId, setOrganizationId] = React.useState('')
  const [roles, setRoles] = React.useState<string[]>(editing ? dialog.affiliation.roles : [])
  const [message, setMessage] = React.useState<string | null>(null)
  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: () => getJSON<OrganizationsResponse>('/api/v1/organizations'),
    enabled: !editing,
    retry: false,
  })
  const saveAffiliation = useMutation<AffiliationResponse, Error>({
    mutationFn: () => {
      if (editing) {
        return patchJSON<AffiliationResponse>(`/api/v1/affiliations/${dialog.affiliation.id}`, {
          affiliation: { roles },
        })
      }

      return postJSON<AffiliationResponse>(`/api/v1/persons/${person.id}/affiliations`, {
        affiliation: { organization_id: Number(organizationId), roles },
      })
    },
    onSuccess: async () => {
      setMessage(null)
      await invalidateAffiliationQueries(queryClient, {
        personId: person.id,
        organizationId: editing ? dialog.affiliation.organization_id : Number(organizationId),
      })
      onClose()
    },
    onError: (error) => setMessage(affiliationMutationMessage(error)),
  })
  const endAffiliation = useMutation<void, Error>({
    mutationFn: () => {
      if (!editing) throw new Error('No affiliation selected.')
      return deleteJSON(`/api/v1/affiliations/${dialog.affiliation.id}`)
    },
    onSuccess: async () => {
      if (!editing) return

      setMessage(null)
      await invalidateAffiliationQueries(queryClient, {
        personId: person.id,
        organizationId: dialog.affiliation.organization_id,
      })
      onClose()
    },
    onError: (error) => setMessage(affiliationMutationMessage(error)),
  })
  const targetName = editing ? dialog.affiliation.organization : 'an organization'
  const hasSelectedOrganization = editing || organizationId !== ''
  const mutationPending = saveAffiliation.isPending || endAffiliation.isPending
  const submitDisabled = mutationPending || !hasSelectedOrganization

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (submitDisabled) return

            saveAffiliation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit affiliation' : 'Add affiliation'}</DialogTitle>
            <DialogDescription>
              Link {person.name} to {targetName} and choose roles there.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {message && <Alert>{message}</Alert>}
            {!editing && (
              <AffiliationOrganizationSelect
                organizations={organizationsQuery.data?.organizations ?? []}
                value={organizationId}
                onChange={setOrganizationId}
              />
            )}
            <AffiliationRoleCheckboxes roles={roles} onChange={setRoles} idPrefix="person-aff" />
          </div>
          <DialogFooter>
            {editing && (
              <Button
                type="button"
                variant="outline"
                className="mr-auto"
                disabled={endAffiliation.isPending || saveAffiliation.isPending}
                onClick={() => endAffiliation.mutate()}
              >
                End affiliation
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {editing ? 'Save affiliation' : 'Add affiliation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AffiliationOrganizationSelect({
  organizations,
  value,
  onChange,
}: {
  organizations: Organization[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="affiliation-organization">Organization</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="affiliation-organization">
          <SelectValue placeholder="Select an organization" />
        </SelectTrigger>
        <SelectContent>
          {organizations.map((organization) => (
            <SelectItem key={organization.id} value={String(organization.id)}>
              {organization.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
